import { AuxButtonTypeEnum, AuxButtonSizeEnum } from "@blk/aladdin-web-components";
import { replace } from "connected-react-router";
import { useMemo } from "react";
import * as React from "react";
import { useDispatch } from "react-redux";
import { InsUploadButton } from "../common/insUploadButton";
import { InsDownloadPrivateEquityBenchmarks } from "./insDownloadPrivateEquityBenchmarks";
import { PrivateBenchmarkRequestType, useGetPrivateBenchmarks, usePostCustomPrivateBenchmarks } from "api/admin/src";
import { is2xxStatusCode, is4xxStatusCode } from "common/number-helper";
import { getPrivateBenchmarkRows } from "components/config/grid-templates/private-benchmarks-list";
import { MessagesDialogFunctionName } from "components/frame/helper";
import { InsQueryStatedElement } from "components/widgets/base/insQueryStatedElement";
import { CustomColumnType } from "components/widgets/grid/grid-column-types";
import { InsGrid } from "components/widgets/grid/insGrid";
import { usePermission, Permissions } from "feature-flag";
import { useRefreshOnUserSettingsChange } from "hooks/widget-data/user-settings/user-settings";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { setSelectedRowsState } from "store/grids";
import "../administration.scss";
import "./ins-private-equity-benchmark.scss";
import { setMessagesDialog } from "store/messages-dialog";
import { getCurrentLocalTime } from "common/date-utils";
const getExportComponent = (isCustom, stateId) => {
    if (isCustom === true) {
        return React.createElement(React.Fragment, null,
            React.createElement("span", null, language.en.skeleton.navigation.administration.whatToUpload),
            React.createElement(InsDownloadPrivateEquityBenchmarks, { templateOnly: true, stateId: stateId, isCustom: isCustom, appInsightsOperationName: appInsightEvents.customEvents.names.DownloadBenchmarkTemplate }),
            React.createElement(InsDownloadPrivateEquityBenchmarks, { templateOnly: false, stateId: stateId, isCustom: isCustom, appInsightsOperationName: appInsightEvents.customEvents.names.ExportCustomBenchmark }));
    }
    return React.createElement(InsDownloadPrivateEquityBenchmarks, { templateOnly: false, stateId: stateId, isCustom: isCustom, appInsightsOperationName: appInsightEvents.customEvents.names.BenchmarkExportSelection });
};
const getImportComponent = (mutation, forceRequestCallback, dispatch, disabled) => {
    const indicesLang = language.en.administration.marketDataAdministration.indices;
    return React.createElement(InsUploadButton, { accept: ".xls,.xlsx", isDisabled: disabled, appInsightsEventName: appInsightEvents.customEvents.names.ImportCustomBenchmark, onChanged: async (e) => {
            if (e.detail.newValue.length && !disabled) {
                const [file] = e.detail.newValue;
                if (file.isValid) {
                    const importStatusLang = indicesLang.privateEquityBenchmarks.importStatus;
                    await mutation(file, {})?.then((value) => {
                        if (is4xxStatusCode(value?.status)) {
                            const responseBodyErrors = value.body;
                            dispatch(setMessagesDialog({
                                functionName: MessagesDialogFunctionName.Default,
                                messages: Object.values(responseBodyErrors?.errors || {}).flat()
                                    .map((m) => ({ message: m })),
                                title: importStatusLang.failure
                            }));
                        }
                        else if (is2xxStatusCode(value?.status)) {
                            const responseBodyBenchmarks = value.body;
                            const importedBenchmarks = responseBodyBenchmarks?.length;
                            if (importedBenchmarks) {
                                dispatch(setMessagesDialog({
                                    functionName: MessagesDialogFunctionName.Default,
                                    messages: [{ message: importStatusLang.benchmarkImported(importedBenchmarks) }],
                                    title: importStatusLang.success
                                }));
                            }
                            else {
                                dispatch(setMessagesDialog({
                                    functionName: MessagesDialogFunctionName.Default,
                                    messages: [{ message: importStatusLang.noBenchmarks }],
                                    title: importStatusLang.information
                                }));
                            }
                        }
                    });
                    forceRequestCallback();
                }
            }
        }, label: indicesLang.privateEquityBenchmarks.importBenchmarks, buttonType: AuxButtonTypeEnum.SECONDARY, buttonSize: AuxButtonSizeEnum.SMALL });
};
const cols = [
    {
        field: "checkboxCol",
        floatingFilter: false,
        headerCheckboxSelection: true,
        headerName: " ",
        type: CustomColumnType.AUX_CHECKBOX_COLUMN,
        width: 32
    },
    {
        field: "name",
        headerName: "Benchmark name",
        sort: "asc",
        type: [CustomColumnType.InsLinkColumn]
    },
    { field: "source", headerName: "Source", type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "sizeClass", headerName: "Size Class", hide: true, type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { cellDataType: false, field: "vintage", headerName: "Vintage", type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "geography", headerName: "Geography", type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "strategy", headerName: "Strategy", type: [CustomColumnType.AUX_TEXT_COLUMN] }
];
export const InsPrivateEquityBenchmarksContent = (props) => {
    const dispatch = useDispatch(), [, mutation] = usePostCustomPrivateBenchmarks(), uploadBenchmarkPermission = usePermission([Permissions.UPLOAD_CUSTOM_BENCHMARK]), [fetchedRows, querystate, forceRequestCallback] = useGetPrivateBenchmarks({ type: PrivateBenchmarkRequestType.All }, {
        update: { PrivateBenchmarks: (_, transformedValue) => ({ ...transformedValue }) }
    });
    useRefreshOnUserSettingsChange();
    const rowData = useMemo(() => {
        const rows = fetchedRows.filter((x) => props.isCustom || (x?.summary?.source === props?.source || null));
        let rowsWithLinks = rows.map((r) => ({
            ...r.summary,
            name: {
                label: r.summary?.name,
                onClick: () => {
                    appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.OpenBenchmarkDetails,
                        properties: { LocalTimestamp: getCurrentLocalTime() } }, {
                        benchmarkName: r.summary?.name
                    });
                    dispatch(replace(`/administration/market-data/private-equity-benchmarks/${r.summary?.id}?isCustom=${r.summary?.isCustom?.toString()}`));
                }
            }
        }));
        const indicesLang = language.en.administration.marketDataAdministration.indices;
        if (props.allowDelete === true) {
            rowsWithLinks = rowsWithLinks.map((r) => ({
                ...r,
                deleteIcon: {
                    icon: "delete",
                    onClick: () => {
                        dispatch(setMessagesDialog({ customProp: { id: r.id ?? "", name: r?.name?.label ?? "" },
                            functionName: MessagesDialogFunctionName.PrivateEquityBenchmarkContent,
                            hasCustomFooter: true,
                            messages: [
                                { message: indicesLang.privateEquityBenchmarks.deleteConfirmation(r?.name?.label ?? "") },
                                { message: indicesLang.privateEquityBenchmarks.actionCannotBeUndone }
                            ],
                            title: indicesLang.privateEquityBenchmarks.deleteHeader,
                            type: "alert-with-options" }));
                    },
                    size: AuxButtonSizeEnum.SMALL,
                    type: AuxButtonTypeEnum.TERTIARY_SUBTLE
                }
            }));
        }
        return getPrivateBenchmarkRows(rowsWithLinks, (r) => (r?.isCustom || false) === (props?.isCustom || false));
    }, [fetchedRows, props.allowDelete, props.isCustom, props?.source, dispatch]);
    let columnDefs = cols;
    if (props.allowDelete === true) {
        columnDefs = columnDefs.concat([
            {
                colId: "delete",
                field: "deleteIcon",
                filter: false,
                headerName: language.en.common.delete,
                type: [CustomColumnType.AUX_BUTTON_COLUMN, CustomColumnType.InsCenterAllignedColumn],
                width: 100
            }
        ]);
    }
    return React.createElement(React.Fragment, null,
        React.createElement("div", { className: "tabHeaderRight" },
            props.allowExport === true && getExportComponent(props.isCustom, props.stateId),
            props.allowImport === true && getImportComponent(mutation, forceRequestCallback, dispatch, !uploadBenchmarkPermission)),
        React.createElement("div", { className: "defaultIndicesGrid" },
            React.createElement(InsQueryStatedElement, { queryState: querystate },
                React.createElement(InsGrid, { stateId: props.stateId, rowData: rowData, queryState: querystate, applyColumnsAutosize: true, selectionMode: props?.allowExport ? "multiple" : "single", rowSelectedHandler: ((e) => {
                        dispatch(setSelectedRowsState({ id: props.stateId, selectedRows: e.api.getSelectedRows().map((row) => row?.id?.toString()) }));
                    }), columnDefs: columnDefs }))));
};
